import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Printer, Plus, Trash2, Edit, Check, X, Wifi, WifiOff,
  TestTube, Download, RefreshCw, Settings, Monitor, ChefHat,
  Flame, Salad, Pizza, Coffee, AlertCircle, Key, Copy, Eye, EyeOff,
  Laptop, FileText, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { BackButton } from '@/components/ui/back-button';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Station icons
const STATION_ICONS = {
  hauptkasse: Monitor,
  kueche: ChefHat,
  fritteuse: Flame,
  salat: Salad,
  pizza: Pizza,
  getraenke: Coffee,
  grill: Flame
};

// Station names
const STATION_NAMES = {
  hauptkasse: 'Hauptkasse',
  kueche: 'Küche',
  fritteuse: 'Fritteuse',
  salat: 'Salatstation',
  pizza: 'Pizza-Station',
  getraenke: 'Getränke',
  grill: 'Grill'
};

// ============================================
// MAIN PRINTER SETTINGS COMPONENT
// ============================================
export default function PrinterSettings({ sessionToken, onClose }) {
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printServiceConnected, setPrintServiceConnected] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [documentAssignments, setDocumentAssignments] = useState([]);
  
  // View state - 'printers' or 'documents'
  const [activeView, setActiveView] = useState('printers');
  
  // API Key state
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState(null);
  const [configuringPrinter, setConfiguringPrinter] = useState(null);
  
  // Config form state
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [configStationType, setConfigStationType] = useState('');
  const [configPaperWidth, setConfigPaperWidth] = useState('80');
  const [configPrinterType, setConfigPrinterType] = useState('local');
  
  // Form refs - using uncontrolled inputs to prevent focus loss
  const nameRef = useRef(null);
  const ipRef = useRef(null);
  const portRef = useRef(null);
  
  // Select values still need state but won't cause input focus loss
  const [printerType, setPrinterType] = useState('bon');
  const [stationType, setStationType] = useState('hauptkasse');
  const [paperWidth, setPaperWidth] = useState('80');
  const [enabled, setEnabled] = useState(true);

  // Fetch API Key
  const fetchApiKey = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/cashx/my-api-key`, {
        headers: { 'X-Session-Token': sessionToken }
      });
      if (response.ok) {
        const data = await response.json();
        setApiKey(data.api_key || '');
      }
    } catch (error) {
      console.error('Fetch API key error:', error);
    }
  }, [sessionToken]);

  // Copy API Key to clipboard
  const copyApiKey = () => {
    navigator.clipboard.writeText(apiKey);
    toast.success('API-Key in Zwischenablage kopiert!');
  };

  // Fetch printers
  const fetchPrinters = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/cashx/printers/`, {
        headers: { 'X-Session-Token': sessionToken }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPrinters(data.printers || []);
        setPrintServiceConnected(data.print_service_connected || false);
        setAvailableCategories(data.available_categories || []);
        setDocumentTypes(data.document_types || []);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Drucker');
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  // Fetch document assignments
  const fetchDocumentAssignments = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/cashx/printers/document-assignments`, {
        headers: { 'X-Session-Token': sessionToken }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDocumentAssignments(data.assignments || []);
      }
    } catch (error) {
      console.error('Error fetching document assignments:', error);
    }
  }, [sessionToken]);

  useEffect(() => {
    fetchPrinters();
    fetchApiKey();
    fetchDocumentAssignments();
  }, [fetchPrinters, fetchApiKey, fetchDocumentAssignments]);

  // Update document assignment - supports multiple printers
  const updateDocumentAssignment = (docType, printerId) => {
    setDocumentAssignments(prev => {
      const existing = prev.find(a => a.document_type === docType);
      if (existing) {
        return prev.map(a => a.document_type === docType 
          ? { ...a, printer_id: printerId || null }
          : a
        );
      }
      return [...prev, { document_type: docType, printer_id: printerId || null, printer_ids: [], enabled: true, copies: 1 }];
    });
  };

  // Toggle printer in multi-select for document type
  const togglePrinterForDocType = (docType, printerId) => {
    setDocumentAssignments(prev => {
      const existing = prev.find(a => a.document_type === docType);
      if (existing) {
        const currentPrinters = existing.printer_ids || (existing.printer_id ? [existing.printer_id] : []);
        const newPrinters = currentPrinters.includes(printerId)
          ? currentPrinters.filter(p => p !== printerId)
          : [...currentPrinters, printerId];
        
        return prev.map(a => a.document_type === docType 
          ? { ...a, printer_ids: newPrinters, printer_id: newPrinters[0] || null }
          : a
        );
      }
      return [...prev, { document_type: docType, printer_id: printerId, printer_ids: [printerId], enabled: true, copies: 1 }];
    });
  };

  // Update copies for document type
  const updateDocumentCopies = (docType, copies) => {
    setDocumentAssignments(prev => {
      const existing = prev.find(a => a.document_type === docType);
      if (existing) {
        return prev.map(a => a.document_type === docType 
          ? { ...a, copies: parseInt(copies) || 1 }
          : a
        );
      }
      return [...prev, { document_type: docType, printer_id: null, printer_ids: [], enabled: true, copies: parseInt(copies) || 1 }];
    });
  };

  // Save all document assignments
  const saveDocumentAssignments = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/cashx/printers/document-assignments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': sessionToken
        },
        body: JSON.stringify({ assignments: documentAssignments })
      });
      
      if (response.ok) {
        toast.success('Belegtyp-Zuordnungen gespeichert!');
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };

  // Open config modal for a printer
  const openConfigModal = (printer) => {
    setConfiguringPrinter(printer);
    setSelectedCategories(printer.categories || []);
    setConfigStationType(printer.station_type || '');
    setConfigPaperWidth((printer.paper_width || 80).toString());
    setConfigPrinterType(printer.printer_type || 'local');
    setShowConfigModal(true);
  };

  // Save printer configuration
  const saveConfiguration = async () => {
    if (!configuringPrinter) return;
    
    try {
      const endpoint = configuringPrinter.is_local 
        ? `${API_BASE}/api/cashx/printers/local/configure`
        : `${API_BASE}/api/cashx/printers/${configuringPrinter.printer_id}`;
      
      const method = configuringPrinter.is_local ? 'POST' : 'PUT';
      
      const body = configuringPrinter.is_local
        ? {
            printer_name: configuringPrinter.name,
            categories: selectedCategories,
            station_type: configStationType || null,
            paper_width: parseInt(configPaperWidth),
            printer_type: configPrinterType
          }
        : {
            categories: selectedCategories,
            station_type: configStationType || null,
            paper_width: parseInt(configPaperWidth),
            printer_type: configPrinterType
          };
      
      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': sessionToken
        },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        toast.success('Drucker-Konfiguration gespeichert!');
        setShowConfigModal(false);
        setConfiguringPrinter(null);
        fetchPrinters();
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };

  // Toggle category selection
  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(c => c !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Reset form
  const resetForm = () => {
    if (nameRef.current) nameRef.current.value = '';
    if (ipRef.current) ipRef.current.value = '';
    if (portRef.current) portRef.current.value = '9100';
    setPrinterType('bon');
    setStationType('hauptkasse');
    setPaperWidth('80');
    setEnabled(true);
  };

  // Populate form for editing
  const populateForm = (printer) => {
    // Use setTimeout to ensure refs are available after modal opens
    setTimeout(() => {
      if (nameRef.current) nameRef.current.value = printer.name || '';
      if (ipRef.current) ipRef.current.value = printer.ip_address || '';
      if (portRef.current) portRef.current.value = (printer.port || 9100).toString();
      setPrinterType(printer.printer_type || 'bon');
      setStationType(printer.station_type || 'hauptkasse');
      setPaperWidth((printer.paper_width || 80).toString());
      setEnabled(printer.enabled !== false);
    }, 50);
  };

  // Get form data from refs
  const getFormData = () => {
    return {
      name: nameRef.current?.value || '',
      ip_address: ipRef.current?.value || '',
      port: parseInt(portRef.current?.value) || 9100,
      printer_type: printerType,
      station_type: stationType,
      paper_width: parseInt(paperWidth),
      enabled: enabled
    };
  };

  // Add printer handler
  const handleAddPrinter = async () => {
    const formData = getFormData();
    
    if (!formData.name || !formData.ip_address) {
      toast.error('Name und IP-Adresse sind erforderlich');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/api/cashx/printers/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': sessionToken
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success('Drucker hinzugefügt!');
        setShowAddModal(false);
        resetForm();
        fetchPrinters();
      } else {
        toast.error('Fehler beim Hinzufügen');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };

  // Update printer handler
  const handleUpdatePrinter = async () => {
    if (!editingPrinter) return;
    
    const formData = getFormData();
    
    if (!formData.name || !formData.ip_address) {
      toast.error('Name und IP-Adresse sind erforderlich');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/api/cashx/printers/${editingPrinter.printer_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': sessionToken
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success('Drucker aktualisiert!');
        setShowEditModal(false);
        setEditingPrinter(null);
        resetForm();
        fetchPrinters();
      } else {
        toast.error('Fehler beim Aktualisieren');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };

  // Delete printer
  const handleDeletePrinter = async (printerId) => {
    if (!confirm('Drucker wirklich löschen?')) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/cashx/printers/${printerId}`, {
        method: 'DELETE',
        headers: { 'X-Session-Token': sessionToken }
      });
      
      if (response.ok) {
        toast.success('Drucker gelöscht!');
        fetchPrinters();
      } else {
        toast.error('Fehler beim Löschen');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };

  // Test printer
  const handleTestPrint = async (printerId) => {
    try {
      const response = await fetch(`${API_BASE}/api/cashx/printers/${printerId}/test`, {
        method: 'POST',
        headers: { 'X-Session-Token': sessionToken }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Testdruck gesendet!');
      } else {
        toast.error(data.message || 'Testdruck fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };

  // Open add modal
  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  // Open edit modal
  const openEditModal = (printer) => {
    setEditingPrinter(printer);
    setShowEditModal(true);
    populateForm(printer);
  };

  // Form JSX - shared between add and edit modals
  const renderForm = () => (
    <div className="space-y-4">
      <div>
        <Label className="text-gray-300">Drucker-Name</Label>
        <input
          ref={nameRef}
          type="text"
          placeholder="z.B. Küchen-Drucker 1"
          className="flex h-10 w-full rounded-md border border-gray-600 bg-gray-700 px-3 py-2 text-sm text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent mt-1"
          autoComplete="off"
          data-testid="printer-name-input"
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-gray-300">IP-Adresse</Label>
          <input
            ref={ipRef}
            type="text"
            placeholder="192.168.1.100"
            className="flex h-10 w-full rounded-md border border-gray-600 bg-gray-700 px-3 py-2 text-sm text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent mt-1"
            autoComplete="off"
            data-testid="printer-ip-input"
          />
        </div>
        <div>
          <Label className="text-gray-300">Port</Label>
          <input
            ref={portRef}
            type="number"
            defaultValue="9100"
            className="flex h-10 w-full rounded-md border border-gray-600 bg-gray-700 px-3 py-2 text-sm text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent mt-1"
            data-testid="printer-port-input"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-gray-300">Drucker-Typ</Label>
          <Select value={printerType} onValueChange={setPrinterType}>
            <SelectTrigger className="bg-gray-700 border-gray-600 text-white mt-1" data-testid="printer-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700">
              <SelectItem value="bon">Bon-Drucker (Kasse)</SelectItem>
              <SelectItem value="kitchen">Küchen-Drucker</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-gray-300">Papierbreite</Label>
          <Select value={paperWidth} onValueChange={setPaperWidth}>
            <SelectTrigger className="bg-gray-700 border-gray-600 text-white mt-1" data-testid="printer-width-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700">
              <SelectItem value="58">58mm</SelectItem>
              <SelectItem value="80">80mm</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div>
        <Label className="text-gray-300">Station zuordnen</Label>
        <Select value={stationType} onValueChange={setStationType}>
          <SelectTrigger className="bg-gray-700 border-gray-600 text-white mt-1" data-testid="printer-station-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-700">
            {Object.entries(STATION_NAMES).map(([key, stationName]) => (
              <SelectItem key={key} value={key}>{stationName}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div className="flex items-center justify-between">
        <Label className="text-gray-300">Drucker aktiviert</Label>
        <Switch
          checked={enabled}
          onCheckedChange={setEnabled}
          data-testid="printer-enabled-switch"
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header with Tabs */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {onClose && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="gap-2 text-gray-400 hover:text-white hover:bg-gray-800/50 mr-2"
              data-testid="printer-settings-back-button"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück
            </Button>
          )}
          <Printer className="w-6 h-6 text-amber-400" />
          <h2 className="text-xl font-bold text-white">Drucker-Verwaltung</h2>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Tab Buttons */}
          <div className="flex bg-gray-800 rounded-lg p-1 mr-4">
            <Button
              variant={activeView === 'printers' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveView('printers')}
              className={activeView === 'printers' ? 'bg-amber-600' : 'text-gray-400'}
            >
              <Laptop className="w-4 h-4 mr-1" />
              Drucker
            </Button>
            <Button
              variant={activeView === 'documents' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveView('documents')}
              className={activeView === 'documents' ? 'bg-amber-600' : 'text-gray-400'}
            >
              <FileText className="w-4 h-4 mr-1" />
              Belegtypen
            </Button>
          </div>
        <div className="flex items-center gap-3">
          {/* Print Service Status */}
          <Badge 
            variant="outline" 
            className={printServiceConnected 
              ? 'bg-green-500/20 text-green-400 border-green-500' 
              : 'bg-red-500/20 text-red-400 border-red-500'}
          >
            {printServiceConnected ? (
              <><Wifi className="w-3 h-3 mr-1" /> Print-Service verbunden</>
            ) : (
              <><WifiOff className="w-3 h-3 mr-1" /> Print-Service offline</>
            )}
          </Badge>
          
          {activeView === 'printers' && (
            <Button
              onClick={openAddModal}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Drucker hinzufügen
            </Button>
          )}
        </div>
        </div>
      </div>

      {/* ==================== PRINTERS VIEW ==================== */}
      {activeView === 'printers' && (
        <>
      {/* API Key Section */}
      <Card className="bg-blue-500/10 border-blue-500/30">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Key className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-blue-300 font-medium mb-2">Ihr API-Key für den Print-Service</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-800 rounded-lg p-2 font-mono text-sm overflow-hidden">
                  {showApiKey ? (
                    <span className="text-white break-all">{apiKey || 'Laden...'}</span>
                  ) : (
                    <span className="text-gray-500">{'•'.repeat(32)}</span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="text-gray-400 hover:text-white"
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyApiKey}
                  className="text-blue-400 hover:text-blue-300"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-gray-500 text-xs mt-2">
                Diesen Key in die print-service.js Datei eintragen
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Print Service Info */}
      {!printServiceConnected && (
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-300 font-medium">Print-Service nicht verbunden</p>
                <p className="text-amber-200/70 text-sm mt-1">
                  Für automatisches Drucken muss der Print-Service auf dem Haupt-PC laufen.
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-amber-500 text-amber-400 hover:bg-amber-500/20"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = '/downloads/cashx-print-service.zip';
                      link.download = 'cashx-print-service.zip';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      toast.success('Download gestartet! Siehe README.txt für Anleitung.');
                    }}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Print-Service herunterladen
                  </Button>
                </div>
                <div className="mt-3 p-2 bg-gray-800/50 rounded text-xs text-gray-400">
                  <p className="font-medium text-gray-300 mb-1">Kurzanleitung:</p>
                  <ol className="list-decimal list-inside space-y-0.5">
                    <li>ZIP herunterladen & entpacken</li>
                    <li>API-Key (oben) in print-service.js eintragen</li>
                    <li>Node.js installieren (nodejs.org)</li>
                    <li>start-print-service.bat ausführen</li>
                  </ol>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Local Printers Section (from Print Service) */}
      {printServiceConnected && printers.some(p => p.is_local) && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Laptop className="w-5 h-5 text-green-400" />
            <h3 className="text-lg font-semibold text-white">Lokale Drucker (vom PC)</h3>
            <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500 text-xs">
              Automatisch erkannt
            </Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {printers.filter(p => p.is_local).map((printer) => (
              <Card 
                key={printer.printer_id}
                className="bg-green-500/10 border-green-500/30"
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-green-500/20">
                        {printer.is_default ? (
                          <FileText className="w-5 h-5 text-green-400" />
                        ) : (
                          <Laptop className="w-5 h-5 text-green-400" />
                        )}
                      </div>
                      <div>
                        <h3 className="text-white font-medium">{printer.name}</h3>
                        <p className="text-gray-400 text-sm">{printer.driver || 'Windows-Drucker'}</p>
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end gap-1">
                      {printer.is_default && (
                        <Badge variant="outline" className="bg-blue-500/20 text-blue-400 border-blue-500 text-xs">
                          Standard
                        </Badge>
                      )}
                      <Badge variant="outline" className={printer.status === 'ready' ? 'text-green-400 border-green-500/30' : 'text-yellow-400 border-yellow-500/30'}>
                        {printer.status === 'ready' ? 'Bereit' : 'Offline'}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Show assigned categories */}
                  {printer.categories && printer.categories.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {printer.categories.map(catId => {
                        const cat = availableCategories.find(c => c.id === catId);
                        return cat ? (
                          <Badge key={catId} variant="outline" className="text-xs bg-gray-700/50 text-gray-300 border-gray-600">
                            {cat.icon} {cat.name}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="mt-4 flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleTestPrint(printer.printer_id)}
                      className="border-green-500 text-green-400 hover:bg-green-500/20"
                    >
                      <TestTube className="w-4 h-4 mr-1" />
                      Testdruck
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openConfigModal(printer)}
                      className="border-amber-500 text-amber-400 hover:bg-amber-500/20"
                    >
                      <Settings className="w-4 h-4 mr-1" />
                      Konfigurieren
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Network Printers Section */}
      <div className="space-y-3">
        {printers.some(p => p.is_local) && (
          <div className="flex items-center gap-2">
            <Wifi className="w-5 h-5 text-amber-400" />
            <h3 className="text-lg font-semibold text-white">Netzwerk-Drucker (manuell)</h3>
          </div>
        )}
        
        {/* Printers List */}
        {loading ? (
          <div className="text-center py-8 text-gray-400">Lade Drucker...</div>
        ) : printers.filter(p => !p.is_local).length === 0 ? (
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-8 text-center">
              <Printer className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Keine Netzwerk-Drucker konfiguriert</p>
              <p className="text-gray-500 text-sm mt-1">
                Fügen Sie einen Netzwerk-Drucker (ESC/POS) hinzu, oder nutzen Sie die lokalen Drucker oben
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {printers.filter(p => !p.is_local).map((printer) => {
              const StationIcon = STATION_ICONS[printer.station_type] || Monitor;
              
              return (
                <Card 
                  key={printer.printer_id}
                  className={`bg-gray-800/80 border-gray-700 ${!printer.enabled ? 'opacity-50' : ''}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${printer.printer_type === 'bon' ? 'bg-amber-500/20' : 'bg-red-500/20'}`}>
                          <Printer className={`w-5 h-5 ${printer.printer_type === 'bon' ? 'text-amber-400' : 'text-red-400'}`} />
                        </div>
                        <div>
                          <h3 className="text-white font-medium">{printer.name}</h3>
                          <p className="text-gray-400 text-sm">{printer.ip_address}:{printer.port}</p>
                        </div>
                      </div>
                      
                      <Badge variant="outline" className={printer.enabled ? 'text-green-400 border-green-500/30' : 'text-gray-500 border-gray-600'}>
                        {printer.enabled ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                    </div>
                    
                    {/* Station Assignment */}
                    <div className="mt-3 flex items-center gap-2">
                      <StationIcon className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-400 text-sm">
                        {STATION_NAMES[printer.station_type] || 'Nicht zugeordnet'}
                      </span>
                      <Badge variant="outline" className="text-gray-500 border-gray-600 text-xs">
                        {printer.paper_width}mm
                      </Badge>
                    </div>
                    
                    {/* Actions */}
                    <div className="mt-4 flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleTestPrint(printer.printer_id)}
                        className="border-blue-500 text-blue-400 hover:bg-blue-500/20"
                        disabled={!printServiceConnected}
                      >
                        <TestTube className="w-4 h-4 mr-1" />
                        Test
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditModal(printer)}
                        className="border-gray-600 text-gray-400 hover:bg-gray-700"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Bearbeiten
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeletePrinter(printer.printer_id)}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      </div>
      </>
      )}

      {/* ==================== DOCUMENT TYPES VIEW ==================== */}
      {activeView === 'documents' && (
        <div className="space-y-6">
          {/* Info Card */}
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-blue-300 font-medium">Belegtyp-Drucker-Zuordnung</p>
                  <p className="text-blue-200/70 text-sm mt-1">
                    Legen Sie fest, welche Drucker welche Belege drucken. Sie können <strong>mehrere Drucker</strong> pro Belegtyp auswählen 
                    (z.B. Küchenbons an mehrere Stationen). Stellen Sie auch die Anzahl der Kopien ein.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Document Type Assignments - Table Style like reference image */}
          <Card className="bg-gray-800/80 border-gray-700">
            <CardHeader className="border-b border-gray-700 py-3">
              <div className="grid grid-cols-12 text-sm font-medium text-gray-400">
                <div className="col-span-4">Berichtstyp</div>
                <div className="col-span-5">Drucker (mehrere möglich)</div>
                <div className="col-span-2 text-center">Kopien</div>
                <div className="col-span-1 text-center">Format</div>
              </div>
            </CardHeader>
            <CardContent className="p-0 divide-y divide-gray-700">
              {documentTypes.map((docType) => {
                const assignment = documentAssignments.find(a => a.document_type === docType.id);
                const assignedPrinterIds = assignment?.printer_ids?.length > 0 
                  ? assignment.printer_ids 
                  : (assignment?.printer_id ? [assignment.printer_id] : []);
                const copies = assignment?.copies || 1;
                
                return (
                  <div key={docType.id} className="grid grid-cols-12 items-center py-3 px-4 hover:bg-gray-700/30">
                    {/* Document Type */}
                    <div className="col-span-4 flex items-center gap-3">
                      <span className="text-xl">{docType.icon}</span>
                      <div>
                        <h3 className="text-white font-medium text-sm">{docType.name}</h3>
                        <p className="text-gray-500 text-xs">{docType.description}</p>
                      </div>
                    </div>
                    
                    {/* Multi-Printer Selection */}
                    <div className="col-span-5">
                      <div className="flex flex-wrap gap-1">
                        {printers.filter(p => p.enabled !== false).map((printer) => {
                          const isSelected = assignedPrinterIds.includes(printer.printer_id);
                          return (
                            <button
                              key={printer.printer_id}
                              onClick={() => togglePrinterForDocType(docType.id, printer.printer_id)}
                              className={`px-2 py-1 rounded text-xs flex items-center gap-1 transition-all ${
                                isSelected 
                                  ? 'bg-amber-500/30 text-amber-300 border border-amber-500' 
                                  : 'bg-gray-700 text-gray-400 border border-gray-600 hover:border-gray-500'
                              }`}
                            >
                              {printer.is_local ? (
                                <Laptop className="w-3 h-3" />
                              ) : (
                                <Printer className="w-3 h-3" />
                              )}
                              {printer.name}
                              {isSelected && <Check className="w-3 h-3 ml-1" />}
                            </button>
                          );
                        })}
                        {printers.filter(p => p.enabled !== false).length === 0 && (
                          <span className="text-gray-500 text-xs">Keine Drucker verfügbar</span>
                        )}
                      </div>
                      {assignedPrinterIds.length === 0 && (
                        <p className="text-gray-500 text-xs mt-1">— Nicht drucken —</p>
                      )}
                    </div>
                    
                    {/* Copies */}
                    <div className="col-span-2 flex justify-center">
                      <Select
                        value={copies.toString()}
                        onValueChange={(value) => updateDocumentCopies(docType.id, value)}
                      >
                        <SelectTrigger className="w-16 h-8 bg-gray-700 border-gray-600 text-white text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700">
                          {[1, 2, 3, 4, 5].map(n => (
                            <SelectItem key={n} value={n.toString()}>{n}x</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Format Badge */}
                    <div className="col-span-1 flex justify-center">
                      <Badge variant="outline" className={`text-xs ${
                        docType.default_format === 'a4' 
                          ? 'bg-blue-500/20 text-blue-400 border-blue-500' 
                          : 'bg-amber-500/20 text-amber-400 border-amber-500'
                      }`}>
                        {docType.default_format === 'a4' ? 'A4' : 'Bon'}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={saveDocumentAssignments} className="bg-amber-600 hover:bg-amber-700">
              <Check className="w-4 h-4 mr-2" />
              Zuordnungen speichern
            </Button>
          </div>
        </div>
      )}

      {/* Add Printer Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white" data-testid="add-printer-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-amber-400" />
              Drucker hinzufügen
            </DialogTitle>
          </DialogHeader>
          
          {renderForm()}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)} data-testid="cancel-add-printer">
              Abbrechen
            </Button>
            <Button onClick={handleAddPrinter} className="bg-amber-600 hover:bg-amber-700" data-testid="submit-add-printer">
              <Check className="w-4 h-4 mr-2" />
              Hinzufügen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Printer Modal */}
      <Dialog open={showEditModal} onOpenChange={(open) => {
        setShowEditModal(open);
        if (!open) setEditingPrinter(null);
      }}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white" data-testid="edit-printer-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-400" />
              Drucker bearbeiten
            </DialogTitle>
          </DialogHeader>
          
          {renderForm()}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)} data-testid="cancel-edit-printer">
              Abbrechen
            </Button>
            <Button onClick={handleUpdatePrinter} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-edit-printer">
              <Check className="w-4 h-4 mr-2" />
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Printer Configuration Modal */}
      <Dialog open={showConfigModal} onOpenChange={(open) => {
        setShowConfigModal(open);
        if (!open) setConfiguringPrinter(null);
      }}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="config-printer-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-amber-400" />
              Drucker konfigurieren
            </DialogTitle>
          </DialogHeader>
          
          {configuringPrinter && (
            <div className="space-y-4">
              <div className="bg-gray-700/50 rounded-lg p-3">
                <p className="text-white font-medium">{configuringPrinter.name}</p>
                <p className="text-gray-400 text-sm">{configuringPrinter.driver || configuringPrinter.ip_address}</p>
              </div>
              
              {/* Printer Type and Paper Width */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300 mb-2 block">Drucker-Typ</Label>
                  <Select value={configPrinterType} onValueChange={setConfigPrinterType}>
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      <SelectItem value="bon">🧾 Bon-Drucker (Kasse)</SelectItem>
                      <SelectItem value="kitchen">👨‍🍳 Küchen-Drucker</SelectItem>
                      <SelectItem value="a4">📄 A4-Drucker</SelectItem>
                      <SelectItem value="local">💻 Standard Windows</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300 mb-2 block">Papierbreite</Label>
                  <Select value={configPaperWidth} onValueChange={setConfigPaperWidth}>
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      <SelectItem value="58">58mm (Thermodruck schmal)</SelectItem>
                      <SelectItem value="80">80mm (Thermodruck standard)</SelectItem>
                      <SelectItem value="210">DIN A4 (210mm)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="border-t border-gray-700 pt-4">
                <Label className="text-gray-300 mb-2 block">Produktkategorien für Küchenbons</Label>
                <p className="text-gray-500 text-xs mb-3">Nur relevant für Küchendrucker. Wählen Sie die Kategorien, die an diesen Drucker gesendet werden.</p>
                
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {availableCategories.map((category) => (
                    <div
                      key={category.id}
                      onClick={() => toggleCategory(category.id)}
                      className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                        selectedCategories.includes(category.id)
                          ? 'bg-amber-500/20 border border-amber-500'
                          : 'bg-gray-700/50 border border-gray-600 hover:bg-gray-700'
                      }`}
                    >
                      <span className="text-lg">{category.icon}</span>
                      <span className={`text-sm ${selectedCategories.includes(category.id) ? 'text-amber-300' : 'text-gray-300'}`}>
                        {category.name}
                      </span>
                      {selectedCategories.includes(category.id) && (
                        <Check className="w-4 h-4 text-amber-400 ml-auto" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <p className="text-blue-300 text-sm">
                  <strong>Tipp:</strong> Wenn ein Drucker keine Kategorien zugewiesen hat, 
                  erhält er alle Bestellungen. Weisen Sie z.B. "Burger" der Küche und "Pommes" der Fritteuse zu.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>
              Abbrechen
            </Button>
            <Button onClick={saveConfiguration} className="bg-amber-600 hover:bg-amber-700">
              <Check className="w-4 h-4 mr-2" />
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
